if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      this.lunifiqueApplyMappedInitialMedia();
    }

    lunifiqueMediaNumericId(value) {
      return String(value || '').split('-').pop().split('/').pop();
    }

    lunifiqueReadVariantMediaMap() {
      const script = document.getElementById(`VariantMediaMap-${this.dataset.section}`);
      if (!script || !script.textContent.trim()) return null;
      try {
        const parsed = JSON.parse(script.textContent);
        let map = parsed && parsed.map !== undefined ? parsed.map : parsed;
        if (typeof map === 'string' && map.trim()) map = JSON.parse(map);
        return map && typeof map === 'object' ? map : null;
      } catch (_error) {
        return null;
      }
    }

    lunifiqueSelectedVariant() {
      const root = document.getElementById(`MainProduct-${this.dataset.section}`);
      const picker = root && root.querySelector('variant-selects');
      const script = picker && picker.querySelector('script[type="application/json"]');
      if (!picker || !script) return null;
      let variants = [];
      try { variants = JSON.parse(script.textContent) || []; } catch (_error) { return null; }
      const selectedOptions = Array.from(picker.querySelectorAll('select, input[type="radio"]:checked')).map((input) => input.value);
      return variants.find((variant) => variant.options && variant.options.every((option, index) => option === selectedOptions[index])) || variants[0] || null;
    }

    lunifiqueMappedIdsForVariant(map, variant) {
      if (!map || !map.variants || !variant || !variant.id) return [];
      const variantGid = String(variant.id).startsWith('gid://') ? String(variant.id) : `gid://shopify/ProductVariant/${variant.id}`;
      const ids = [...(Array.isArray(map.global) ? map.global : []), ...(Array.isArray(map.variants[variantGid]) ? map.variants[variantGid] : [])]
        .map((id) => this.lunifiqueMediaNumericId(id))
        .filter(Boolean);
      return [...new Set(ids)];
    }

    lunifiqueSrcForMediaId(mediaId) {
      const slide = this.querySelector(`[data-media-id="${this.dataset.section}-${mediaId}"]`);
      const img = slide && slide.querySelector('img');
      return img ? String(img.currentSrc || img.src || img.getAttribute('src') || img.getAttribute('srcset') || img.getAttribute('data-src') || img.getAttribute('data-srcset') || '').toUpperCase() : '';
    }

    lunifiqueHeroId(mappedIds) {
      const fallbackId = mappedIds[0] || '';
      let nonMiniatureFallback = '';
      for (const mediaId of mappedIds) {
        const src = this.lunifiqueSrcForMediaId(mediaId);
        if (src.includes('EYE')) return mediaId;
        if (!nonMiniatureFallback && !src.includes('MINIATURE') && !src.includes('ARGUMENT')) nonMiniatureFallback = mediaId;
      }
      return nonMiniatureFallback || fallbackId;
    }

    lunifiqueReorder(parent, selector, attr, orderedIds) {
      if (!parent || !orderedIds.length) return;
      const order = Object.fromEntries(orderedIds.map((id, index) => [id, index]));
      Array.from(parent.querySelectorAll(selector)).sort((a, b) => {
        const aOrder = order[this.lunifiqueMediaNumericId(a.getAttribute(attr))] ?? 9999;
        const bOrder = order[this.lunifiqueMediaNumericId(b.getAttribute(attr))] ?? 9999;
        return aOrder - bOrder;
      }).forEach((item) => {
        const id = this.lunifiqueMediaNumericId(item.getAttribute(attr));
        item.classList.toggle('hidden', !orderedIds.includes(id));
        parent.appendChild(item);
      });
    }

    lunifiqueApplyMappedInitialMedia() {
      const map = this.lunifiqueReadVariantMediaMap();
      const variant = this.lunifiqueSelectedVariant();
      const mappedIds = this.lunifiqueMappedIdsForVariant(map, variant);
      if (!mappedIds.length) {
        this.dataset.lqMediaReady = 'true';
        return;
      }
      const heroId = this.lunifiqueHeroId(mappedIds);
      const orderedIds = heroId ? [heroId, ...mappedIds.filter((id) => id !== heroId)] : mappedIds.slice();
      this.lunifiqueReorder(this.querySelector(`#Slider-Gallery-${this.dataset.section}`), '.product__media-item', 'data-media-id', orderedIds);
      this.lunifiqueReorder(this.querySelector(`#Slider-Thumbnails-${this.dataset.section}`), '.thumbnail-list__item', 'data-target', orderedIds);
      const activeMediaId = `${this.dataset.section}-${heroId}`;
      const activeSlide = this.elements.viewer && this.elements.viewer.querySelector('.product__media-item.is-active');
      const activeNumericId = activeSlide ? this.lunifiqueMediaNumericId(activeSlide.dataset.mediaId) : '';
      const activeIsHero = activeNumericId === heroId && activeSlide && !activeSlide.classList.contains('hidden');
      if (!activeIsHero && this.elements.viewer && this.elements.viewer.querySelector(`[data-media-id="${activeMediaId}"]`)) {
        this.setActiveMedia(activeMediaId, false, false);
      }
      this.dataset.lqMediaReady = 'true';
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

// Lunifique: legacy preserve-first mapped media scheduler removed.
// main-product.liquid owns variant media sync; this asset only sets initial EYE-priority order.
